import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Assessment,
  AssessmentDetail,
  Paginated,
  ResponseListItem,
  ResponseStatus,
  StandardVersionDetail,
} from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Dialog,
  Field,
  Input,
  LoadingPane,
  PageHeader,
  ProgressBar,
  RESPONSE_STATUS_META,
  Select,
  StatusBadge,
  Table,
  formatDate,
  useToast,
} from '../../components/ui.js';
import { ClauseTree } from './components/ClauseTree.js';
import './assessments.css';

export function AssessmentDetailPage() {
  const { assessmentId = '' } = useParams();
  const { org } = useOrg();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();

  const selectedClauseId = params.get('clause');
  const statusFilter = params.get('status') ?? '';
  const [search, setSearch] = useState('');

  const detail = useQuery({
    queryKey: ['assessment', assessmentId],
    queryFn: () => apiFetch<AssessmentDetail>(`/assessments/${assessmentId}`),
  });
  const responses = useQuery({
    queryKey: ['responses', assessmentId],
    queryFn: () =>
      apiFetch<Paginated<ResponseListItem>>(`/assessments/${assessmentId}/responses`, {
        query: { limit: 500 },
      }),
  });
  const versionDetail = useQuery({
    queryKey: ['version-detail', detail.data?.standardVersionId],
    queryFn: () =>
      apiFetch<StandardVersionDetail>(`/standard-versions/${detail.data!.standardVersionId}`),
    enabled: !!detail.data?.standardVersionId,
  });

  const [lockOpen, setLockOpen] = useState(false);
  const lock = useMutation({
    mutationFn: () => apiFetch<Assessment>(`/assessments/${assessmentId}/lock`, { method: 'POST' }),
    onSuccess: () => {
      toast('Assessment locked. All responses are now read-only.', 'success');
      setLockOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['assessment', assessmentId] });
      void queryClient.invalidateQueries({ queryKey: ['responses', assessmentId] });
    },
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Could not lock the assessment', 'error'),
  });

  const [cycleOpen, setCycleOpen] = useState(false);
  const [cycleName, setCycleName] = useState('');
  const newCycle = useMutation({
    mutationFn: () =>
      apiFetch<Assessment>(`/assessments/${assessmentId}/new-cycle`, {
        method: 'POST',
        body: cycleName.trim() ? { name: cycleName.trim() } : {},
      }),
    onSuccess: (created) => {
      toast('New cycle created with your previous answers carried over.', 'success');
      void queryClient.invalidateQueries({ queryKey: ['assessments', org.orgId] });
      navigate(`/assessments/${created.id}`);
    },
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Could not start a new cycle', 'error'),
  });

  const filtered = useMemo(() => {
    let items = responses.data?.items ?? [];
    if (selectedClauseId) {
      // The tree filters by subtree: include descendants via clause number prefix.
      const version = versionDetail.data;
      const findNumber = (id: string): string | null => {
        const walk = (nodes: NonNullable<typeof version>['clauses']): string | null => {
          for (const n of nodes) {
            if (n.id === id) return n.clauseNumber;
            const found = walk(n.children);
            if (found) return found;
          }
          return null;
        };
        return version ? walk(version.clauses) : null;
      };
      const number = findNumber(selectedClauseId);
      if (number) {
        items = items.filter(
          (r) => r.clauseNumber === number || r.clauseNumber.startsWith(`${number}.`),
        );
      } else {
        items = items.filter((r) => r.clauseId === selectedClauseId);
      }
    }
    if (statusFilter) items = items.filter((r) => r.status === statusFilter);
    if (search.trim()) {
      const needle = search.toLowerCase();
      items = items.filter((r) => r.questionText.toLowerCase().includes(needle));
    }
    return items;
  }, [responses.data, selectedClauseId, statusFilter, search, versionDetail.data]);

  if (detail.isLoading || responses.isLoading) return <LoadingPane label="Loading assessment" />;
  if (!detail.data) return null;
  const a = detail.data;
  const progress = a.progress;
  const isAssessor = org.role === 'assessor';
  const isAdmin = org.role === 'admin';
  const allAssessed =
    progress.total > 0 && (progress.byStatus.assessed ?? 0) + (progress.byStatus.locked ?? 0) === progress.total;

  const setFilter = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(updates)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };

  return (
    <div>
      <p>
        <Link to="/assessments">← All assessments</Link>
      </p>
      <PageHeader
        title={a.name}
        subtitle={
          <>
            {a.standardName} <Badge>{a.versionLabel}</Badge>{' '}
            <Badge tone={a.status === 'open' ? 'info' : 'neutral'}>{a.status}</Badge>
            {a.targetDate && <> · target {formatDate(a.targetDate)}</>}
          </>
        }
        actions={
          <>
            <Link to={`/assessments/${assessmentId}/review`}>
              <Button variant="secondary">Review queue</Button>
            </Link>
            {/* Only a standard the switch is on for has a Statement of
                Applicability (standards.supportsSoa, carried on the
                assessment); its endpoints are member-only besides. */}
            {!isAssessor && a.standardSupportsSoa && (
              <Link
                to={`/standards/versions/${a.standardVersionId}/soa?assessmentId=${assessmentId}`}
              >
                <Button variant="secondary">Statement of Applicability</Button>
              </Link>
            )}
            {!isAssessor && (
              <Link to={`/exports?assessment=${assessmentId}`}>
                <Button variant="secondary">Export</Button>
              </Link>
            )}
            {isAssessor && a.status === 'open' && (
              <Button
                onClick={() => setLockOpen(true)}
                disabled={!allAssessed}
                title={
                  allAssessed
                    ? 'Lock the assessment'
                    : 'Every response must be marked as assessed before locking'
                }
              >
                Lock assessment
              </Button>
            )}
            {isAdmin && a.status === 'locked' && (
              <Button onClick={() => setCycleOpen(true)}>Start new cycle</Button>
            )}
          </>
        }
      />

      {a.status === 'locked' && (
        <div className="locked-banner">
          <Badge tone="neutral">Locked</Badge>
          <span>
            This assessment was locked {formatDate(a.lockedAt)}. Content is read-only; start a new
            cycle to make amendments.
          </span>
        </div>
      )}

      <div className="stat-row">
        <div className="stat-card">
          <div className="value">{progress.completionPercent}%</div>
          <div className="label">complete ({progress.total} questions)</div>
          <ProgressBar percent={progress.completionPercent} label="Overall completion" />
        </div>
        <div className={`stat-card ${progress.evidenceMissing > 0 ? 'warn' : 'good'}`}>
          <div className="value">{progress.evidenceMissing}</div>
          <div className="label">questions missing required evidence</div>
        </div>
        <div className={`stat-card ${progress.openReviewerComments > 0 ? 'warn' : ''}`}>
          <div className="value">{progress.openReviewerComments}</div>
          <div className="label">open reviewer comments</div>
        </div>
        <div className={`stat-card ${progress.openAssessorComments > 0 ? 'warn' : ''}`}>
          <div className="value">{progress.openAssessorComments}</div>
          <div className="label">open assessor comments</div>
        </div>
      </div>

      <div className="workspace">
        {versionDetail.data ? (
          <ClauseTree
            clauses={versionDetail.data.clauses}
            responses={responses.data?.items ?? []}
            selectedClauseId={selectedClauseId}
            onSelect={(id) => setFilter({ clause: id })}
          />
        ) : (
          <div />
        )}

        <div>
          <div className="filter-bar">
            <Input
              aria-label="Search questions"
              placeholder="Search questions…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => setFilter({ status: e.target.value || null })}
            >
              <option value="">All statuses</option>
              {Object.entries(RESPONSE_STATUS_META).map(([value, meta]) => (
                <option key={value} value={value}>
                  {meta.label}
                </option>
              ))}
            </Select>
          </div>

          <Table
            head={
              <>
                <th>Clause</th>
                <th>Question</th>
                <th>Status</th>
                <th>Evidence</th>
                <th>Comments</th>
                <th>Updated</th>
              </>
            }
          >
            {filtered.map((r) => (
              <tr key={r.id}>
                <td style={{ whiteSpace: 'nowrap' }}>{r.clauseNumber}</td>
                <td>
                  <Link to={`/assessments/${assessmentId}/responses/${r.id}`}>
                    {r.questionText.length > 120
                      ? `${r.questionText.slice(0, 120)}…`
                      : r.questionText}
                  </Link>
                </td>
                <td>
                  <StatusBadge status={r.status as ResponseStatus} reviewState={r.reviewState} />
                </td>
                <td>
                  {r.requiredEvidence && r.evidenceCount === 0 ? (
                    <Badge tone="warning">required</Badge>
                  ) : (
                    r.evidenceCount || '—'
                  )}
                </td>
                <td>
                  {r.openReviewerComments + r.openAssessorComments > 0 ? (
                    <Badge tone="danger">{r.openReviewerComments + r.openAssessorComments} open</Badge>
                  ) : (
                    '—'
                  )}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(r.updatedAt)}</td>
              </tr>
            ))}
          </Table>
          {filtered.length === 0 && (
            <p style={{ color: 'var(--color-text-muted)', padding: 16 }}>
              No questions match the current filters.
            </p>
          )}
        </div>
      </div>

      <Dialog open={lockOpen} onClose={() => setLockOpen(false)} title="Lock this assessment?">
        <p>
          Locking finalises the assessment outcome: every response and its evidence becomes
          permanently read-only. Amendments will require a new assessment cycle.
        </p>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setLockOpen(false)}>
            Cancel
          </Button>
          <Button variant="danger" loading={lock.isPending} onClick={() => lock.mutate()}>
            Lock assessment
          </Button>
        </div>
      </Dialog>

      <Dialog open={cycleOpen} onClose={() => setCycleOpen(false)} title="Start a new cycle">
        <p>
          A new assessment is created against the same standard version. The latest answer for
          every question is carried over as an editable draft, ready for this year's review.
        </p>
        <Field label="New cycle name" hint={`Leave blank for "${a.name} (new cycle)"`}>
          {(props) => (
            <Input {...props} value={cycleName} onChange={(e) => setCycleName(e.target.value)} />
          )}
        </Field>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setCycleOpen(false)}>
            Cancel
          </Button>
          <Button loading={newCycle.isPending} onClick={() => newCycle.mutate()}>
            Create new cycle
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
